import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  Avatar,
} from '@mui/material';
import {
  FlightTakeoff,
  CardMembership,
  Download,
  QrCode2,
  CheckCircle,
  Warning,
  Person,
  Email,
  Phone,
  Edit,
  Payment,
  MenuBook,
  AccountBalance,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import PersonalDetailsForm from '../../components/pilot/PersonalDetailsForm';
import ClubBoardView from '../../components/pilot/ClubBoardView';
import FederationMembershipForm from '../../components/pilot/FederationMembershipForm';

interface PilotRegistration {
  id: string;
  year: number;
  nome: string;
  cognome: string;
  email: string;
  telefono?: string;
  club_name: string;
  club_id: string;
  club_fee?: number;
  club_payment_info?: string;
  attestato_nr?: string;
  tipo?: string;
  scadenza_visita_medica?: string;
  codice?: string;
  pagato: boolean;
  card_generated_at?: string;
  created_at: string;
  iscrizione: boolean;
  fivl: boolean;
  adesione_nr?: number;
  adesione_file_url?: string;
  pagamento_file_url?: string;
}

const PilotDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState<PilotRegistration[]>([]);
  const [registrationYear, setRegistrationYear] = useState<number | null>(null);
  const [personalDetails, setPersonalDetails] = useState<any>(null);
  const [editingPersonalDetails, setEditingPersonalDetails] = useState(false);

  // Fetch the registration year from the backend
  useEffect(() => {
    const fetchRegistrationYear = async () => {
      try {
        const response = await api.get('/registration-year');
        setRegistrationYear(response.data.year);
      } catch (err) {
        console.error('Failed to fetch registration year:', err);
        // Fallback to current year if endpoint fails
        setRegistrationYear(new Date().getFullYear());
      }
    };
    fetchRegistrationYear();
  }, []);

  const fetchMyRegistrations = useCallback(async () => {
    if (!registrationYear) return;

    try {
      setLoading(true);

      // Fetch current year registrations for logged-in pilot
      const response = await api.get(`/pilot/my-registrations`, {
        params: { year: registrationYear }
      });

      setRegistrations(response.data);
    } catch (err: any) {
      console.error('Failed to fetch registrations:', err);
    } finally {
      setLoading(false);
    }
  }, [registrationYear]);

  const fetchPersonalDetails = useCallback(async () => {
    try {
      const response = await api.get('/personal-details');
      setPersonalDetails(response.data);
    } catch (err) {
      console.error('Failed to fetch personal details:', err);
      // It's ok if personal details don't exist yet
    }
  }, []);

  useEffect(() => {
    if ((currentUser?.user_type === 'pilot' || currentUser?.is_pilot) && registrationYear) {
      fetchMyRegistrations();
      fetchPersonalDetails();
    }
  }, [currentUser, registrationYear, fetchMyRegistrations, fetchPersonalDetails]);

  const handlePersonalDetailsUpdate = async (details: any) => {
    setPersonalDetails(details);
    setEditingPersonalDetails(false);
    // Optionally refresh registrations if needed
  };


  const downloadCard = async (registrationId: string) => {
    try {
      const response = await api.get(`/get-pdf/${registrationId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `card_${registrationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download card:', err);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(i18n.language);
  };

  const renderRegistrationCard = (reg: PilotRegistration) => (
    <Card key={reg.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Box sx={{ flex: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <FlightTakeoff />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {reg.nome} {reg.cognome}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reg.club_name}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Email sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{reg.email}</Typography>
                </Box>
                {reg.telefono && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Phone sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{reg.telefono}</Typography>
                  </Box>
                )}
                {reg.attestato_nr && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CardMembership sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {t('pilot.dashboard.licenseNumber')}: {reg.attestato_nr}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ flex: 1 }}>
                {reg.tipo && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('pilot.dashboard.gliderType')}:</strong> {reg.tipo}
                  </Typography>
                )}
                {reg.scadenza_visita_medica && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('pilot.dashboard.medicalExpiry')}:</strong> {formatDate(reg.scadenza_visita_medica)}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>{t('pilot.dashboard.registrationDate')}:</strong> {formatDate(reg.created_at)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {reg.codice && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <QrCode2 sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                  {reg.codice}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Chip
                label={reg.pagato ? t('pilot.dashboard.paid') : t('pilot.dashboard.unpaid')}
                color={reg.pagato ? 'success' : 'warning'}
                icon={reg.pagato ? <CheckCircle /> : <Warning />}
                size="small"
              />
              {reg.iscrizione && (
                <Chip
                  label={t('pilot.dashboard.registered')}
                  color="primary"
                  size="small"
                />
              )}
              {reg.fivl && (
                <Chip
                  label="FIVL"
                  color="secondary"
                  size="small"
                />
              )}
            </Box>

            {/* Payment Information - shown when not paid */}
            {!reg.pagato && (reg.club_fee || reg.club_payment_info) && (
              <Box sx={{
                bgcolor: 'warning.light',
                borderRadius: 1,
                p: 2,
                mb: 2,
                width: '100%',
                maxWidth: 280,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Payment sx={{ color: 'warning.dark', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                    {t('pilot.dashboard.paymentInfo')}
                  </Typography>
                </Box>
                {reg.club_fee && reg.club_fee > 0 && (
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {t('pilot.dashboard.amountDue')}: €{reg.club_fee.toFixed(2)}
                  </Typography>
                )}
                {reg.club_payment_info && (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-line',
                      fontSize: '0.8rem',
                      color: 'text.secondary'
                    }}
                  >
                    {reg.club_payment_info}
                  </Typography>
                )}
              </Box>
            )}

            {reg.card_generated_at ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 200 }}>
                <Button
                  variant="contained"
                  startIcon={<CardMembership />}
                  onClick={() => window.open(`/card/${reg.id}`, '_blank')}
                  size="small"
                  fullWidth
                  sx={{ 
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a67d8 30%, #68356b 90%)',
                    }
                  }}
                >
                  {i18n.language === 'it' ? 'Visualizza Tessera' : 'View Card'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => downloadCard(reg.id)}
                  size="small"
                  fullWidth
                >
                  {t('pilot.dashboard.downloadCard')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1,
                maxWidth: 200,
                width: '100%'
              }}>
                <CardMembership sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {i18n.language === 'it' ? 'Tessera non ancora disponibile' : 'Card not available yet'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {i18n.language === 'it' ? 'In attesa di generazione' : 'Pending generation'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading || registrationYear === null) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 80, height: 80 }}>
            <FlightTakeoff sx={{ fontSize: 40 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
              {t('pilot.dashboard.welcome')}, {currentUser?.nickname || currentUser?.username || 'Pilot'}
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
              {t('pilot.dashboard.title')} - {registrationYear || new Date().getFullYear()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<Person />}
                label={`${t('pilot.dashboard.userType')}: ${t('pilot.dashboard.pilot')}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
              <Chip
                icon={<CardMembership />}
                label={`${registrations.length} ${t('pilot.dashboard.membershipCards')}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
              <Chip
                icon={<Email />}
                label={currentUser?.email_verified ? t('pilot.dashboard.emailVerified') : t('pilot.dashboard.emailNeedsVerification')}
                sx={{
                  bgcolor: currentUser?.email_verified ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<MenuBook />}
            onClick={() => navigate('/app/logbook')}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            {t('logbook.title')}
          </Button>
        </Box>
      </Paper>

      {/* Personal Details Card */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" />
            {t('pilot.dashboard.personalInfo')}
          </Typography>
          {!editingPersonalDetails && (
            <Button
              startIcon={<Edit />}
              onClick={() => setEditingPersonalDetails(true)}
              variant="outlined"
              size="small"
            >
              {personalDetails ? t('pilot.dashboard.editDetails') : t('pilot.dashboard.addDetails')}
            </Button>
          )}
        </Box>

        {editingPersonalDetails ? (
          <PersonalDetailsForm
            initialData={personalDetails}
            onComplete={handlePersonalDetailsUpdate}
            isRegistrationFlow={false}
          />
        ) : personalDetails ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
                {t('pilot.dashboard.basicInfo')}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('pilot.dashboard.fullName')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                {personalDetails.nome} {personalDetails.cognome}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('pilot.dashboard.email')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                {currentUser?.email || '-'}
              </Typography>
              
              {personalDetails.telefono && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('pilot.dashboard.phone')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {personalDetails.telefono}
                  </Typography>
                </>
              )}

              {personalDetails.codice_fiscale && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('pilot.dashboard.taxCode')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {personalDetails.codice_fiscale}
                  </Typography>
                </>
              )}
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
                {t('pilot.dashboard.flightInfo')}
              </Typography>
              
              {personalDetails.attestato_nr && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('pilot.dashboard.licenseNumber')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {personalDetails.attestato_nr}
                  </Typography>
                </>
              )}
              
              {personalDetails.tipo && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('pilot.dashboard.gliderType')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {personalDetails.tipo}
                  </Typography>
                </>
              )}
              
              {personalDetails.scadenza_visita_medica && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('pilot.dashboard.medicalExpiry')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {formatDate(personalDetails.scadenza_visita_medica)}
                  </Typography>
                </>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('pilot.dashboard.emailStatus')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  size="small"
                  icon={<Email />}
                  label={currentUser?.email_verified ? t('pilot.dashboard.emailVerified') : t('pilot.dashboard.emailNeedsVerification')}
                  color={currentUser?.email_verified ? 'success' : 'warning'}
                  variant={currentUser?.email_verified ? 'filled' : 'outlined'}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('pilot.dashboard.noPersonalDetails')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setEditingPersonalDetails(true)}
            >
              {t('pilot.dashboard.addPersonalDetails')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* My Membership Cards Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CardMembership color="primary" />
          {t('pilot.dashboard.myMembershipCards')} ({registrations.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('pilot.dashboard.membershipCardsDescription')}
        </Typography>
        
        {registrations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FlightTakeoff sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('pilot.dashboard.noMembershipCards')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pilot.dashboard.noMembershipCardsDescription')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<FlightTakeoff />}
              onClick={() => navigate('/pilot-registration')}
              sx={{ 
                px: 4, 
                py: 1.5,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a67d8 30%, #68356b 90%)',
                }
              }}
            >
              {t('pilot.dashboard.registerNow')}
            </Button>
          </Box>
        ) : (
          <Box>
            {registrations.map(reg => renderRegistrationCard(reg))}
          </Box>
        )}
      </Paper>

      {/* Federation Membership Section - only show if pilot has registrations */}
      {registrations.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance color="primary" />
            {t('pilot.federation.sectionTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('pilot.federation.sectionDescription')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {registrations.map(reg => (
              <FederationMembershipForm
                key={reg.id}
                registration={reg}
                onSuccess={fetchMyRegistrations}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Club Board Members Section - only show if pilot has registrations */}
      {registrations.length > 0 && (
        <ClubBoardView
          clubIds={Array.from(new Set(registrations.map(reg => reg.club_id)))}
          clubNames={registrations.reduce((acc, reg) => {
            acc[reg.club_id] = reg.club_name;
            return acc;
          }, {} as Record<string, string>)}
        />
      )}

    </Container>
  );
};

export default PilotDashboard;